import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Mail } from "lucide-react";

const REASONS = [
  { value: "too_many_emails", label: "Too many emails" },
  { value: "not_relevant", label: "Deals weren't relevant to me" },
  { value: "found_better", label: "Found a better source" },
  { value: "not_sign_up", label: "I never signed up" },
  { value: "other", label: "Something else" },
];

export default function Unsubscribed() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", email: "", error: "" });
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [fbSubmitted, setFbSubmitted] = useState(false);
  const [fbSubmitting, setFbSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ status: "error", email: "", error: "Missing unsubscribe token." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/newsletter/unsubscribe", null, { params: { token } });
        setState({ status: "ok", email: data.email || "", removed: data.removed });
      } catch (err) {
        setState({ status: "error", email: "", error: err?.response?.data?.detail || "Something went wrong." });
      }
    })();
  }, [token]);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setFbSubmitting(true);
    try {
      await api.post("/newsletter/unsubscribe-feedback", { token, reason, notes });
      setFbSubmitted(true);
    } catch (err) {
      // Silent — still mark submitted so we don't nag
      setFbSubmitted(true);
    } finally {
      setFbSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-20" data-testid="unsubscribed-page">
      <div className="border-2 border-black bg-white p-8 md:p-10 text-center">
        {state.status === "loading" && (
          <>
            <Mail className="w-10 h-10 mx-auto text-black/30" strokeWidth={1.5} />
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-black/50">Processing…</div>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 mb-3 inline-flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Unsubscribed
            </div>
            <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter leading-tight">
              You&rsquo;re out.<br /><span className="italic font-normal">No hard feelings.</span>
            </h1>
            {state.email && (
              <p className="text-black/60 mt-4 text-sm">
                We&rsquo;ve removed <span className="font-mono">{state.email}</span> from all DealHub emails.
              </p>
            )}
            <div className="mt-8">
              <Link to="/" className="inline-block bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]" data-testid="unsub-home">
                Back to DealHub
              </Link>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3 inline-flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Link expired or invalid
            </div>
            <h1 className="font-serif font-black text-3xl tracking-tighter">Couldn&rsquo;t unsubscribe you</h1>
            <p className="text-black/60 mt-4 text-sm">{state.error}</p>
            <p className="text-black/60 mt-2 text-sm">Reply to any DealHub email and we&rsquo;ll take you off manually.</p>
          </>
        )}
      </div>

      {/* Feedback form — only on successful unsubscribe */}
      {state.status === "ok" && (
        <div className="mt-8 border border-black/10 bg-white p-6 md:p-8" data-testid="unsub-feedback">
          {fbSubmitted ? (
            <div className="text-center" data-testid="feedback-thanks">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 mb-2">Thanks for the note</div>
              <p className="text-sm text-black/60">We read every one. Sincerely — the DealHub team.</p>
            </div>
          ) : (
            <form onSubmit={submitFeedback} className="text-left">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2">One last thing</div>
              <h2 className="font-serif font-bold text-2xl mb-4">Why are you leaving?</h2>
              <div className="space-y-2 mb-4">
                {REASONS.map((r) => (
                  <label key={r.value} className={`flex items-center gap-3 border px-3 py-2 cursor-pointer text-sm ${reason === r.value ? "border-black bg-black/5" : "border-black/15 hover:border-black/40"}`}>
                    <input
                      type="radio"
                      name="reason"
                      value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="accent-black"
                      data-testid={`feedback-reason-${r.value}`}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
              {reason === "other" && (
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Tell us more (optional)"
                  maxLength={500}
                  className="w-full border border-black/20 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-black min-h-[80px]"
                  data-testid="feedback-notes"
                />
              )}
              <div className="flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setFbSubmitted(true)}
                  className="text-xs font-bold uppercase tracking-widest text-black/50 hover:text-black underline underline-offset-4"
                  data-testid="feedback-skip"
                >
                  Skip
                </button>
                <button
                  type="submit"
                  disabled={!reason || fbSubmitting}
                  className="bg-black text-white px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="feedback-submit"
                >
                  {fbSubmitting ? "Sending…" : "Send feedback"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
